import { useMemo, useState, type KeyboardEvent, type MouseEvent } from "react";
import clsx from "clsx";
import { Temporal } from "temporal-polyfill";
import type { ZonedTimeString } from "@poi-planner/shared";
import type { EventTimingProposal } from "../store/uiStore";
import {
  minutesBetween,
  parseZoned,
  wallTimeCandidates,
  zonedDate,
  zonedFromWallTime,
  zonedTimeOfDay,
} from "../utils/zonedTime";
import { FormField, FormInput, FormSelect } from "./FormFields";
import { Button } from "./Button";
import { TripTimezoneSelect } from "./TripTimezoneSelect";
import { timezoneCity } from "../utils/timezoneDisplay";

export interface EventTimeFieldsValue {
  date: string;
  time: string;
  zone: string;
  offset: string | null;
}

export function eventTimeFieldsFromZoned(
  value: ZonedTimeString,
): EventTimeFieldsValue {
  const zoned = parseZoned(value);
  return {
    date: zonedDate(value),
    time: zonedTimeOfDay(value),
    zone: zoned.timeZoneId,
    offset: zoned.offset,
  };
}

export function zonedFromEventTimeFields(
  fields: EventTimeFieldsValue,
): ZonedTimeString | null {
  if (!fields.date || !fields.time || !fields.zone) return null;
  return zonedFromWallTime(
    fields.date,
    fields.time,
    fields.zone,
    fields.offset,
  );
}

export function eventTimeFieldsError(
  fields: EventTimeFieldsValue,
): string | null {
  if (!fields.date || !fields.time || !fields.zone) return null;
  return wallTimeCandidates(fields.date, fields.time, fields.zone).length === 0
    ? `${fields.time} does not exist in ${fields.zone} on ${fields.date} because the clock moves forward.`
    : null;
}

function formatOffset(offset: string): string {
  return `UTC${offset}`;
}

/**
 * One end of a timed event as a labelled card: wall date, time and zone, plus
 * the DST offset choice when the wall time is ambiguous.
 */
function EndpointFields({
  label,
  value,
  onChange,
  disabled,
  sameAsZone,
}: {
  label: "Start" | "End";
  value: EventTimeFieldsValue;
  onChange: (value: EventTimeFieldsValue) => void;
  disabled: boolean;
  sameAsZone?: string;
}) {
  const candidates = useMemo(
    () =>
      value.date && value.time && value.zone
        ? wallTimeCandidates(value.date, value.time, value.zone)
        : [],
    [value.date, value.time, value.zone],
  );
  const error = eventTimeFieldsError(value);
  const selectedOffset =
    candidates.find((candidate) => candidate.offset === value.offset)?.offset ??
    candidates[0]?.offset ??
    "";

  const changeWallField = (
    update: Partial<Pick<EventTimeFieldsValue, "date" | "time" | "zone">>,
  ) => onChange({ ...value, ...update, offset: null });

  const testId = label.toLowerCase();
  const offsetSelect = candidates.length > 1 && (
    <FormSelect
      value={selectedOffset}
      onChange={(offset) => onChange({ ...value, offset })}
      options={candidates.map((candidate) => ({
        value: candidate.offset,
        label: `${candidate.occurrence === "earlier" ? "Earlier" : "Later"} occurrence — ${formatOffset(candidate.offset)}`,
      }))}
      disabled={disabled}
      aria-label={`${label} UTC offset`}
      data-testid={`${testId}-offset-select`}
    />
  );
  const errorAlert = error && (
    <p
      role="alert"
      className="mb-0 mt-2 rounded-lg bg-error-bg px-3 py-2 text-xs font-semibold text-error"
    >
      {error}
    </p>
  );

  return (
    <div className="rounded-xl border border-border bg-surface p-3.5">
      <div className="mb-3 flex items-center gap-2">
        <span className="size-2 rounded-full bg-info" aria-hidden />
        <h5 className="m-0 text-xs font-extrabold uppercase tracking-wider text-secondary">
          {label}
        </h5>
      </div>
      <div className="grid grid-cols-1 gap-3">
        <FormField label={`${label} date`}>
          <FormInput
            value={value.date}
            onChange={(date) => changeWallField({ date })}
            type="date"
            disabled={disabled}
            data-testid={`${testId}-date-input`}
          />
        </FormField>
        <FormField label={`${label} time`}>
          <FormInput
            value={value.time}
            onChange={(time) => changeWallField({ time })}
            type="time"
            disabled={disabled}
            data-testid={`${testId}-time-input`}
          />
        </FormField>
        <FormField label={`${label} timezone`}>
          <TripTimezoneSelect
            value={value.zone}
            date={value.date}
            onChange={(zone) => changeWallField({ zone })}
            disabled={disabled}
            sameAsZone={sameAsZone}
            data-testid={`${testId}-zone-select`}
          />
        </FormField>
        {offsetSelect && (
          <div>
            <FormField label={`${label} UTC offset`}>{offsetSelect}</FormField>
          </div>
        )}
      </div>
      {errorAlert}
    </div>
  );
}

const CHIP_CLASS =
  "relative inline-flex h-8 min-w-0 cursor-pointer items-center rounded-md bg-subtle text-sm font-semibold text-primary transition-colors hover:bg-border has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-info has-[:disabled]:cursor-default";

/** Opens the native date/time picker from anywhere on the chip. */
function openPicker(event: MouseEvent<HTMLInputElement>) {
  try {
    event.currentTarget.showPicker?.();
  } catch {
    // Some browsers refuse outside a trusted gesture; typing still works.
  }
}

function openPickerFromKeyboard(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  try {
    event.currentTarget.showPicker?.();
  } catch {
    // As above.
  }
}

function formatChipDate(date: string): string {
  try {
    return Temporal.PlainDate.from(date).toLocaleString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return date;
  }
}

/** A date that reads as text ("Mon 29 Sep 2025") but edits natively. */
export function DateChip({
  value,
  onChange,
  disabled = false,
  label,
  testId,
  min,
  max,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  label: string;
  testId?: string;
  min?: string;
  max?: string;
}) {
  return (
    // Fixed minimum width so the time chips beside it line up across rows.
    <span className={clsx(CHIP_CLASS, "min-w-[9.5rem] px-2.5")}>
      <span
        aria-hidden
        className={value ? undefined : "font-medium text-tertiary"}
      >
        {value ? formatChipDate(value) : "Add date"}
      </span>
      <input
        type="date"
        value={value}
        min={min}
        max={max}
        onChange={(event) => onChange(event.target.value)}
        onClick={openPicker}
        onKeyDown={openPickerFromKeyboard}
        disabled={disabled}
        aria-label={label}
        data-testid={testId}
        className="picker-chip absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-default"
      />
    </span>
  );
}

function TimeChip({
  value,
  onChange,
  disabled,
  label,
  testId,
}: {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  label: string;
  testId: string;
}) {
  return (
    <span className={clsx(CHIP_CLASS, "px-1")}>
      <input
        type="time"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onClick={openPicker}
        disabled={disabled}
        aria-label={label}
        data-testid={testId}
        className="picker-chip w-[5.75rem] cursor-pointer border-0 bg-transparent px-1.5 text-sm font-semibold tabular-nums text-primary outline-none disabled:cursor-default"
      />
    </span>
  );
}

/** DST offset choice and nonexistent-time error for one end, if needed. */
function EndpointIssues({
  label,
  value,
  onChange,
  disabled,
}: {
  label: "Start" | "End";
  value: EventTimeFieldsValue;
  onChange: (value: EventTimeFieldsValue) => void;
  disabled: boolean;
}) {
  const candidates = useMemo(
    () =>
      value.date && value.time && value.zone
        ? wallTimeCandidates(value.date, value.time, value.zone)
        : [],
    [value.date, value.time, value.zone],
  );
  const error = eventTimeFieldsError(value);
  if (candidates.length <= 1 && !error) return null;
  const selectedOffset =
    candidates.find((candidate) => candidate.offset === value.offset)?.offset ??
    candidates[0]?.offset ??
    "";
  return (
    <div className="mt-2 flex flex-col gap-2">
      {candidates.length > 1 && (
        <FormSelect
          value={selectedOffset}
          onChange={(offset) => onChange({ ...value, offset })}
          options={candidates.map((candidate) => ({
            value: candidate.offset,
            label: `${label}: ${candidate.occurrence === "earlier" ? "earlier" : "later"} occurrence — ${formatOffset(candidate.offset)}`,
          }))}
          disabled={disabled}
          aria-label={`${label} UTC offset`}
          data-testid={`${label.toLowerCase()}-offset-select`}
        />
      )}
      {error && (
        <p
          role="alert"
          className="m-0 rounded-lg bg-error-bg px-3 py-2 text-xs font-semibold text-error"
        >
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Sentence-style timing for the note editor: "Starts Mon 29 Sep · 13:50",
 * each part a chip that opens its picker. One shared time zone covers both
 * ends until the user splits them (flights), when each row gets its own.
 */
export function EventTimingRows({
  start,
  end,
  onStartChange,
  onEndChange,
  disabled = false,
}: {
  start: EventTimeFieldsValue;
  end: EventTimeFieldsValue;
  onStartChange: (value: EventTimeFieldsValue) => void;
  onEndChange: (value: EventTimeFieldsValue) => void;
  disabled?: boolean;
}) {
  const [userSplit, setUserSplit] = useState(false);
  const split = userSplit || start.zone !== end.zone;

  const changeWall = (
    value: EventTimeFieldsValue,
    onChange: (value: EventTimeFieldsValue) => void,
    update: Partial<Pick<EventTimeFieldsValue, "date" | "time" | "zone">>,
  ) => onChange({ ...value, ...update, offset: null });

  const rows = [
    { label: "Start" as const, value: start, onChange: onStartChange },
    { label: "End" as const, value: end, onChange: onEndChange },
  ];

  return (
    <div className="min-w-0">
      <div className="grid grid-cols-1 items-center gap-x-3 gap-y-1.5 sm:grid-cols-[3.25rem_minmax(0,1fr)] sm:gap-y-2">
        {rows.map(({ label, value, onChange }) => {
          const testId = label.toLowerCase();
          return (
            <div key={label} className="contents">
              <span className="flex items-center gap-1.5 text-sm font-semibold text-secondary max-sm:mt-1 max-sm:text-xs">
                {label === "Start" ? "Starts" : "Ends"}
              </span>
              <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                <DateChip
                  value={value.date}
                  onChange={(date) => changeWall(value, onChange, { date })}
                  disabled={disabled}
                  label={`${label} date`}
                  testId={`${testId}-date-input`}
                />
                <TimeChip
                  value={value.time}
                  onChange={(time) => changeWall(value, onChange, { time })}
                  disabled={disabled}
                  label={`${label} time`}
                  testId={`${testId}-time-input`}
                />
                {split && (
                  <TripTimezoneSelect
                    variant="chip"
                    value={value.zone}
                    date={value.date}
                    onChange={(zone) => changeWall(value, onChange, { zone })}
                    disabled={disabled}
                    aria-label={`${label} timezone`}
                    data-testid={`${testId}-zone-select`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {rows.map(({ label, value, onChange }) => (
        <EndpointIssues
          key={label}
          label={label}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      ))}

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 pl-[4rem] text-sm text-secondary max-sm:pl-0">
        {split ? (
          !disabled && (
            <Button
              variant="unstyled"
              onClick={() => {
                setUserSplit(false);
                changeWall(end, onEndChange, { zone: start.zone });
              }}
              className="cursor-pointer bg-transparent p-0 text-sm font-semibold text-info hover:underline"
            >
              Use one time zone
            </Button>
          )
        ) : (
          <>
            <TripTimezoneSelect
              variant="chip"
              value={start.zone}
              date={start.date}
              onChange={(zone) => {
                changeWall(start, onStartChange, { zone });
                changeWall(end, onEndChange, { zone });
              }}
              disabled={disabled}
              aria-label="Time zone"
              data-testid="start-zone-select"
            />
            {!disabled && (
              <Button
                variant="unstyled"
                onClick={() => setUserSplit(true)}
                className="cursor-pointer bg-transparent p-0 text-sm font-semibold text-info hover:underline"
              >
                Ends in another time zone
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function EventTimingFields({
  start,
  end,
  onStartChange,
  onEndChange,
  disabled = false,
}: {
  start: EventTimeFieldsValue;
  end: EventTimeFieldsValue;
  onStartChange: (value: EventTimeFieldsValue) => void;
  onEndChange: (value: EventTimeFieldsValue) => void;
  disabled?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <EndpointFields
        label="Start"
        value={start}
        onChange={onStartChange}
        disabled={disabled}
      />
      <EndpointFields
        label="End"
        value={end}
        onChange={onEndChange}
        disabled={disabled}
        sameAsZone={start.zone}
      />
    </div>
  );
}

export function TimezoneChangeNotice({
  proposal,
}: {
  proposal: EventTimingProposal;
}) {
  const duration = Math.round(
    minutesBetween(proposal.startTime, proposal.endTime),
  );
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-blue-950"
      data-testid="timezone-change-notice"
    >
      <span
        className="material-symbols-outlined mt-px text-lg text-info"
        aria-hidden
      >
        travel_explore
      </span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <strong className="text-sm">Timezone changed</strong>
          <span className="text-xs font-bold text-blue-700">
            {timezoneCity(proposal.originalDisplayZone)} →{" "}
            {timezoneCity(proposal.targetDisplayZone)}
            {` · ${duration} min`}
          </span>
        </div>
        <p className="mb-0 mt-1 text-xs font-medium text-blue-800">
          Review the converted wall times. Nothing changes until you save.
        </p>
      </div>
    </div>
  );
}
